import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react'

import produce from 'immer'
import { assertNever } from 'src/utils/assertNever'

import { CrfFolderTemplates, CrfSection } from './entity'
import { UploadTask } from './task'

type State = {
  folderTemplates: CrfFolderTemplates | undefined
  sections: CrfSection[] | undefined
  files: Record<UploadTask['name'], string>
}

const initialState: State = {
  folderTemplates: undefined,
  sections: undefined,
  files: {
    template: '',
    section: '',
    field: '',
    choice: '',
  },
}

type Action =
  | {
      type: 'uploadFolderTemplates'
      folderTemplates: CrfFolderTemplates
      fileName: string
    }
  | { type: 'uploadSection'; sections: CrfSection[]; fileName: string }
  | { type: 'uploadField'; fileName: string }
  | { type: 'uploadChoice'; fileName: string }

const reducer = (state: State, action: Action): State =>
  produce(state, draft => {
    switch (action.type) {
      case 'uploadFolderTemplates':
        draft.folderTemplates = action.folderTemplates
        draft.files.template = action.fileName
        return
      case 'uploadSection':
        draft.sections = action.sections
        draft.files.section = action.fileName
        return
      case 'uploadField':
        draft.files.field = action.fileName
        return
      case 'uploadChoice':
        draft.files.choice = action.fileName
        return
      default:
        return assertNever(action)
    }
  })

type ContextValue = {
  state: State
  uploadFolderTemplates: (args: {
    folderTemplates: CrfFolderTemplates
    fileName: string
  }) => void
  uploadSection: (args: { sections: CrfSection[]; fileName: string }) => void
  uploadField: (args: { fileName: string }) => void
  uploadChoice: (args: { fileName: string }) => void
}

const CrfUploadContext = createContext<ContextValue | null>(null)

type ProviderProps = {
  children: React.ReactNode
}

export const CrfUploadProvider: React.FC<ProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const uploadFolderTemplates = useCallback(
    (args: { folderTemplates: CrfFolderTemplates; fileName: string }) =>
      dispatch({ type: 'uploadFolderTemplates', ...args }),
    [],
  )
  const uploadSection = useCallback(
    (args: { sections: CrfSection[]; fileName: string }) =>
      dispatch({ type: 'uploadSection', ...args }),
    [],
  )
  const uploadField = useCallback(
    (args: { fileName: string }) => dispatch({ type: 'uploadField', ...args }),
    [],
  )
  const uploadChoice = useCallback(
    (args: { fileName: string }) => dispatch({ type: 'uploadChoice', ...args }),
    [],
  )

  const value = useMemo(
    () => ({
      state,
      uploadFolderTemplates,
      uploadSection,
      uploadField,
      uploadChoice,
    }),
    [state, uploadFolderTemplates, uploadSection, uploadField, uploadChoice],
  )

  return (
    <CrfUploadContext.Provider value={value}>
      {children}
    </CrfUploadContext.Provider>
  )
}

export const useCrfUpload = () => {
  const ctx = useContext(CrfUploadContext)
  if (!ctx) {
    throw new Error('useCrfUpload must be used within CrfUploadProvider')
  }
  return ctx
}
